/**
 * Cognito's SRP password proof, with no library.
 *
 * The in-app sign-in (auth/direct.ts) proves the parent knows their password
 * without ever sending it: Cognito's USER_SRP_AUTH flow, the same one its own
 * SDKs use. The app client deliberately does not allow USER_PASSWORD_AUTH,
 * which would send the password itself (20-api ExplicitAuthFlows).
 *
 * The arithmetic is Secure Remote Password (RFC 5054's 3072-bit group, g = 2)
 * as Cognito's AuthenticationHelper does it, down to its quirks: hex strings
 * padded to whole bytes with a leading 00 when the top bit is set, SHA-256
 * over those bytes, and a one-block HKDF labelled "Caldera Derived Key".
 * srp.test.ts checks it against an independent server-side computation, so a
 * slip here fails a test rather than every parent's sign-in.
 *
 * BigInt and Web Crypto only: nothing to install, nothing that phones home.
 */

const N_HEX =
  'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD129024E088A67CC74020BBEA63B139B22514A08798E3404DD' +
  'EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
  'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3DC2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F' +
  '83655D23DCA3AD961C62F356208552BB9ED529077096966D670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
  'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9DE2BCBF6955817183995497CEA956AE515D2261898FA0510' +
  '15728E5A8AAAC42DAD33170D04507A33A85521ABDF1CBA64ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7' +
  'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6BF12FFA06D98A0864D87602733EC86A64521F2B18177B200C' +
  'BBE117577A615D6C770988C0BAD946E208E24FA074E5AB3143DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF';

export const N = BigInt(`0x${N_HEX}`);
export const g = 2n;

export function modPow(base: bigint, exponent: bigint, modulus: bigint): bigint {
  let result = 1n;
  let b = ((base % modulus) + modulus) % modulus;
  let e = exponent;
  while (e > 0n) {
    if (e & 1n) result = (result * b) % modulus;
    e >>= 1n;
    b = (b * b) % modulus;
  }
  return result;
}

/** Cognito's padHex: whole bytes, and a leading 00 so the number reads as positive. */
export function padHex(value: bigint | string): string {
  let hex = typeof value === 'bigint' ? value.toString(16) : value.toLowerCase();
  if (hex.length % 2 === 1) hex = `0${hex}`;
  else if ('89abcdef'.includes(hex[0]!)) hex = `00${hex}`;
  return hex;
}

export function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

const utf8 = (text: string): Uint8Array => new TextEncoder().encode(text);

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as BufferSource));
}

/** SHA-256 of bytes, as 64 hex digits. */
export async function hashHex(bytes: Uint8Array): Promise<string> {
  return bytesToHex(await sha256(bytes)).padStart(64, '0');
}

async function hmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const imported = await crypto.subtle.importKey('raw', key as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await crypto.subtle.sign('HMAC', imported, data as BufferSource));
}

/** k = H(N | g), as Cognito pads them. */
export async function multiplier(): Promise<bigint> {
  return BigInt(`0x${await hashHex(hexToBytes(padHex(N) + padHex(g)))}`);
}

export interface Ephemeral {
  /** The private half; never leaves this module's caller. */
  a: bigint;
  /** The public half, sent as SRP_A. */
  A: bigint;
}

export function ephemeral(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): Ephemeral {
  for (;;) {
    const a = BigInt(`0x${bytesToHex(random(128))}`) % N;
    const A = modPow(g, a, N);
    if (a !== 0n && A % N !== 0n) return { a, A };
  }
}

/** The pool's short name: the part of the user pool id after the region. */
export function poolName(userPoolId: string): string {
  return userPoolId.split('_')[1] ?? userPoolId;
}

/** x = H(salt | H(poolName | userId | ":" | password)). */
export async function privateKey(pool: string, userId: string, password: string, saltHex: string): Promise<bigint> {
  const inner = await hashHex(utf8(`${pool}${userId}:${password}`));
  return BigInt(`0x${await hashHex(hexToBytes(padHex(saltHex) + inner))}`);
}

/** The shared session key both sides arrive at (16 bytes, Cognito's HKDF). */
export async function sessionKey(input: {
  a: bigint;
  A: bigint;
  B: bigint;
  x: bigint;
}): Promise<Uint8Array> {
  const { a, A, B, x } = input;
  if (B % N === 0n) throw new Error('Sign-in failed. Please try again.');
  const u = BigInt(`0x${await hashHex(hexToBytes(padHex(A) + padHex(B)))}`);
  if (u === 0n) throw new Error('Sign-in failed. Please try again.');
  const k = await multiplier();
  const base = (((B - k * modPow(g, x, N)) % N) + N) % N;
  const S = modPow(base, a + u * x, N);
  const prk = await hmac(hexToBytes(padHex(u)), hexToBytes(padHex(S)));
  const okm = await hmac(prk, concat(utf8('Caldera Derived Key'), new Uint8Array([1])));
  return okm.slice(0, 16);
}

/** "Mon Oct 9 14:05:03 UTC 2026": Cognito's format, day not zero-padded. */
export function cognitoTimestamp(now: Date = new Date()): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const two = (n: number) => String(n).padStart(2, '0');
  return `${days[now.getUTCDay()]} ${months[now.getUTCMonth()]} ${now.getUTCDate()} ` +
    `${two(now.getUTCHours())}:${two(now.getUTCMinutes())}:${two(now.getUTCSeconds())} UTC ${now.getUTCFullYear()}`;
}

function base64ToBytes(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
}

function bytesToBase64(bytes: Uint8Array): string {
  let text = '';
  for (const b of bytes) text += String.fromCharCode(b);
  return btoa(text);
}

/**
 * The answer to Cognito's PASSWORD_VERIFIER challenge.
 *
 * Everything Cognito sent (SALT, SRP_B, SECRET_BLOCK, USER_ID_FOR_SRP) goes
 * in; the four ChallengeResponses come out.
 */
export async function passwordVerifier(input: {
  userPoolId: string;
  password: string;
  ephemeral: Ephemeral;
  challenge: { SALT: string; SRP_B: string; SECRET_BLOCK: string; USER_ID_FOR_SRP: string };
  now?: Date;
}): Promise<Record<string, string>> {
  const pool = poolName(input.userPoolId);
  const userId = input.challenge.USER_ID_FOR_SRP;
  const x = await privateKey(pool, userId, input.password, input.challenge.SALT);
  const key = await sessionKey({ a: input.ephemeral.a, A: input.ephemeral.A, B: BigInt(`0x${input.challenge.SRP_B}`), x });
  const timestamp = cognitoTimestamp(input.now);
  const signature = await hmac(
    key,
    concat(utf8(pool), utf8(userId), base64ToBytes(input.challenge.SECRET_BLOCK), utf8(timestamp)),
  );
  return {
    USERNAME: userId,
    PASSWORD_CLAIM_SECRET_BLOCK: input.challenge.SECRET_BLOCK,
    TIMESTAMP: timestamp,
    PASSWORD_CLAIM_SIGNATURE: bytesToBase64(signature),
  };
}
